import { lazy, Suspense } from "react";
import { Reorder, AnimatePresence } from "framer-motion";
import { useDashboardConfigStore, WidgetConfig } from "@/store/dashboardConfigStore";
import { WIDGET_REGISTRY, WidgetSize } from "@/config/dashboardWidgets";
import { WidgetWrapper } from "./WidgetWrapper";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

// Widget components
import { QuickShortcuts } from "./widgets/QuickShortcuts";
import { AlertsWidget } from "./widgets/AlertsWidget";
import { TopCoursesWidget } from "./widgets/TopCoursesWidget";
import { UpcomingClassesWidget } from "./widgets/UpcomingClassesWidget";
import { RecentMembersWidget } from "./widgets/RecentMembersWidget";
import { DynamicKPICard } from "./widgets/DynamicKPICard";

// Lazy load heavy chart components
const GrowthChart = lazy(() => import("@/components/dashboard/v2/GrowthChart").then(m => ({ default: m.GrowthChart })));
const MemberEvolutionChart = lazy(() => import("@/components/dashboard/v2/MemberEvolutionChart").then(m => ({ default: m.MemberEvolutionChart })));
const OccupationHeatmap = lazy(() => import("@/components/dashboard/v2/OccupationHeatmap").then(m => ({ default: m.OccupationHeatmap })));

// Size to grid column classes mapping
const SIZE_CLASSES: Record<WidgetSize, string> = {
  small: "col-span-1",
  medium: "col-span-1 md:col-span-2",
  large: "col-span-1 md:col-span-2 lg:col-span-3",
  full: "col-span-full",
};

interface WidgetRendererProps {
  period: 'today' | 'week' | 'month' | 'year';
  kpisLoading: boolean;
  growthData: any[];
  growthLoading: boolean;
  memberEvolutionData: any[];
  memberEvolutionLoading: boolean;
  heatmapData: any[];
  heatmapLoading: boolean;
}

// Widget component mapping
function renderWidgetContent(
  widgetId: string, 
  props: WidgetRendererProps
): React.ReactNode {
  const { period, kpisLoading, growthData, growthLoading, memberEvolutionData, memberEvolutionLoading, heatmapData, heatmapLoading } = props;

  // Shortcuts are rendered separately as a group
  if (widgetId.startsWith('shortcut-')) {
    return null;
  }

  // Alert widgets are rendered separately as a group
  if (widgetId.startsWith('alert-')) {
    return null;
  }

  // KPI widgets
  // Un identifiant absent du registre vient d'une configuration enregistrée
  // avant le retrait d'un widget : on ne rend rien, sinon le mode édition
  // afficherait un cadre vide.
  if (widgetId.startsWith('kpi-')) {
    if (!WIDGET_REGISTRY[widgetId]) return null;
    return <DynamicKPICard widgetId={widgetId} loading={kpisLoading} />;
  }

  // Chart widgets
  if (widgetId === 'chart-growth') {
    return (
      <Suspense fallback={<div className="h-64 animate-pulse bg-muted rounded-lg" />}>
        <GrowthChart data={growthData} loading={growthLoading} />
      </Suspense>
    );
  }

  if (widgetId === 'chart-members-evolution') {
    const granularity = period === 'year' ? 'month' : period === 'month' ? 'week' : 'day';
    return (
      <Suspense fallback={<div className="h-64 animate-pulse bg-muted rounded-lg" />}>
        <MemberEvolutionChart data={memberEvolutionData} granularity={granularity} loading={memberEvolutionLoading} />
      </Suspense>
    );
  }

  if (widgetId === 'chart-heatmap') {
    return (
      <Suspense fallback={<div className="h-64 animate-pulse bg-muted rounded-lg" />}>
        <OccupationHeatmap data={heatmapData} loading={heatmapLoading} />
      </Suspense>
    );
  }

  // List widgets
  if (widgetId === 'list-top-courses') {
    return <TopCoursesWidget />;
  }

  if (widgetId === 'list-upcoming-classes') {
    return <UpcomingClassesWidget />;
  }

  if (widgetId === 'list-recent-members') {
    return <RecentMembersWidget />;
  }

  // Default: render nothing for unhandled widgets
  return null;
}

export function WidgetRenderer(props: WidgetRendererProps) {
  const { t } = useTranslation("adminui2");
  const { getVisibleWidgets, reorderWidgets, isEditMode, config } = useDashboardConfigStore();
  
  const visibleWidgets = getVisibleWidgets();
  
  // Group widgets by type for special rendering
  const hasShortcuts = visibleWidgets.some(w => w.id.startsWith('shortcut-'));
  const hasAlerts = visibleWidgets.some(w => w.id.startsWith('alert-'));
  
  // Filter out shortcuts and alerts for main grid (they're rendered separately)
  const mainWidgets = visibleWidgets.filter(
    w => !w.id.startsWith('shortcut-') && !w.id.startsWith('alert-')
  );

  // KPIs should be rendered in a special grid
  const kpiWidgets = mainWidgets.filter(w => w.id.startsWith('kpi-'));
  const otherWidgets = mainWidgets.filter(w => !w.id.startsWith('kpi-'));

  const handleReorder = (newOrder: WidgetConfig[]) => {
    reorderWidgets(newOrder.map(w => w.id));
  };

  return (
    <div className="space-y-6">
      {/* Shortcuts Section */}
      {hasShortcuts && (
        <WidgetWrapper widgetId="shortcuts-group" className="mb-4">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">{t("widgetRenderer.quickAccess")}</h3>
            <QuickShortcuts />
          </div>
        </WidgetWrapper>
      )}

      {/* Alerts Section */}
      {hasAlerts && (
        <WidgetWrapper widgetId="alerts-group" className="mb-4">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">{t("widgetRenderer.alerts")}</h3>
            <AlertsWidget />
          </div>
        </WidgetWrapper>
      )}

      {/* KPIs Grid */}
      {kpiWidgets.length > 0 && (
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
          {kpiWidgets.map((widget) => {
            const content = renderWidgetContent(widget.id, props);
            if (!content) return null;
            
            return (
              <WidgetWrapper key={widget.id} widgetId={widget.id}>
                {content}
              </WidgetWrapper>
            );
          })}
        </div>
      )}

      {/* Other Widgets Grid with Reorder */}
      {isEditMode ? (
        <Reorder.Group
          axis="y"
          values={otherWidgets}
          onReorder={handleReorder}
          className="space-y-4"
        >
          <AnimatePresence>
            {otherWidgets.map((widget) => {
              const content = renderWidgetContent(widget.id, props);
              if (!content) return null;
              const widgetDef = WIDGET_REGISTRY[widget.id];
              const size = widget.size || widgetDef?.defaultSize || 'medium';

              return (
                <Reorder.Item
                  key={widget.id}
                  value={widget}
                  className={cn("cursor-grab active:cursor-grabbing")}
                  whileDrag={{ scale: 1.02, boxShadow: "0 8px 20px rgba(0,0,0,0.12)" }}
                >
                  <WidgetWrapper widgetId={widget.id}>
                    {content}
                  </WidgetWrapper>
                </Reorder.Item>
              );
            })}
          </AnimatePresence>
        </Reorder.Group>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {otherWidgets.map((widget) => {
            const content = renderWidgetContent(widget.id, props);
            if (!content) return null;
            const widgetDef = WIDGET_REGISTRY[widget.id];
            const size = widget.size || widgetDef?.defaultSize || 'medium';

            return (
              <div 
                key={widget.id} 
                className={cn(SIZE_CLASSES[size])}
              >
                {content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
